import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, Cog6ToothIcon, GlobeAltIcon, UserIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '../../../assets/icons/logout.svg?react';
import { SelectItem } from '../../../components/selects/SelectItem';
import { UserContext } from '../../../contexts/UserContext';
import { T } from '../../../translation/src';
import { Icon } from '../../../components/Icon';
import useOpen from '../../../hooks/useOpen';
import { UserSettingModal } from '../UserSettingsModal';

interface Props {
  variant: 'navigation' | 'header';
  forceReload: boolean;
  size: 'small' | 'large';
  onlyIcon?: boolean;
}

const AccountSelect_: React.FC2<Props> = ({ variant, forceReload, size, onlyIcon }) => {
  const currentUser = useContext(UserContext);
  const navigate = useNavigate();
  const { onOpen: openUserSettingsModal, onClose: closeUserSettingsModal, open: UserSettingsModalOpen } = useOpen();

  const onLogout = useCallback(async () => {
    if (forceReload) {
      window.location.href = `${window.location.origin}/logout`;
    } else {
      navigate('/logout');
    }
  }, [forceReload, navigate]);

  const onNavigate = useCallback(
    (path: string) => {
      if (forceReload) {
        window.location.href = `${window.location.origin}${path}`;
      } else {
        navigate(path);
      }
    },
    [forceReload, navigate],
  );

  const onNavigateToProfile = useCallback(() => {
    onNavigate('/me/profile');
  }, [onNavigate]);
  const onNavigateAdminCenter = useCallback(() => onNavigate(`/admin-center`), [onNavigate]);

  const divClassNamesByType = {
    navigation: 'w-full',
    header: 'w-auto',
  };

  const smallElement = size === 'small';

  return (
    <div className={classNames(divClassNamesByType[variant], 'relative')}>
      <Menu as="div" className="relative z-20 inline-block w-full">
        <div>
          <Menu.Button
            className={classNames(
              'inline-flex items-center justify-between w-full py-2 text-sm font-medium group text-eerie-black dark:text-white hover:bg-neon-green-300 dark:hover:bg-black focus:outline-none h-14 rounded',
              smallElement ? 'px-2' : 'px-4',
            )}
          >
            <div className="relative flex items-start justify-start min-w-0">
              {smallElement || onlyIcon ? <Icon icon={UserIcon} className="w-5 h-5 ml-4" /> : <div className="truncate">{currentUser.name}</div>}
            </div>
            <ChevronDownIcon className={classNames('w-5 h-5 -mr-1 text-eerie-black dark:group-hover:text-white dark:text-neon-green-300', smallElement ? 'ml-1' : 'ml-2')} aria-hidden="true" />
          </Menu.Button>
        </div>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={classNames(
              'absolute bg-white dark:bg-[#242424] dark:text-white divide-y divide-black rounded-md shadow-lg ring-1 ring-gray-400 focus:outline-none',
              onlyIcon ? 'w-40' : 'w-64',
              // The sidebar menu opens to the right, so it stays on screen beside the notifications bell.
              variant === 'header' ? 'right-0 origin-top-right top-full mt-7' : 'left-0 origin-bottom-left -translate-y-full -top-2',
            )}
          >
            <div className="px-1 py-1">
              {currentUser.isSuperAdmin && (
                <SelectItem icon={GlobeAltIcon} onClick={onNavigateAdminCenter} className="text-black bg-error-red-500 dark:hover:text-white">
                  <T _str="admin center" swc />
                </SelectItem>
              )}
              <SelectItem icon={UserIcon} onClick={onNavigateToProfile}>
                <T _str="profile" swc />
              </SelectItem>
              <SelectItem icon={Cog6ToothIcon} onClick={openUserSettingsModal}>
                <T _str="settings" swc />
              </SelectItem>
              <SelectItem icon={LogoutIcon} onClick={onLogout}>
                <T _str="logout" swc />
              </SelectItem>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      <UserSettingModal onClose={closeUserSettingsModal} open={UserSettingsModalOpen} />
    </div>
  );
};

export const AccountSelect = memo(AccountSelect_);
